import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions, UseFormPayload } from '@domain-group/react-hooks'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import PageContext from '@contexts/pageContext'

import * as serviceCategoryActions from '@redux/modules/serviceCategory'
import type { AppDispatch } from '@redux/store'
import type { EntityOptions } from '@redux/modules/types'

import defaultFormState, { requiredFields } from '@models/serviceCategory'
import type { ServiceCategoryModel, ServiceModel } from '@models/types'

export const generateUrls = () => ({
  serviceCategoriesIndexUrl: '#/systemManager/serviceCategories',
})

type CreateServiceCategoryPayload = {
  dispatch: AppDispatch,
  serviceCategoryParams: Partial<ServiceCategoryModel>,
  requestOptions?: EntityOptions,
}

const createServiceCategory = (params: CreateServiceCategoryPayload) => {
  const { dispatch, serviceCategoryParams, requestOptions } = params
  const { createServiceCategory: createFn } = serviceCategoryActions

  return dispatch(createFn(serviceCategoryParams, requestOptions))
}

type UpdateServiceCategoryPayload = {
  dispatch: AppDispatch,
  requestOptions?: EntityOptions,
  serviceCategory: ServiceCategoryModel,
  serviceCategoryParams: Partial<ServiceCategoryModel>,
}

const updateServiceCategory = (params: UpdateServiceCategoryPayload) => {
  const {
    serviceCategory, serviceCategoryParams, dispatch, requestOptions,
  } = params
  const { updateServiceCategory: updateFn } = serviceCategoryActions

  const updatedParams = {
    id: serviceCategory.id,
    ...serviceCategoryParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type CreateOrEditServiceCategoryPayload = {
  serviceCategory: ServiceCategoryModel,
  showCreateOrEditServiceCategoryModal: Function,
}

const createOrEditServiceCategory = (params: CreateOrEditServiceCategoryPayload) => new Promise((resolve, reject) => {
  const { serviceCategory, showCreateOrEditServiceCategoryModal } = params

  if (showCreateOrEditServiceCategoryModal){
    const payload = {
      serviceCategory,
    }

    showCreateOrEditServiceCategoryModal(payload)

    resolve({ success: true, result: payload })
  }

  reject(new Error('showCreateOrEditServiceCategoryModal not defined in PageContext callbacks'))
})

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useServiceCategoryForm(
  serviceCategory: Partial<ServiceCategoryModel> = {},
  options: UseFormOptions & CustomFormOptions = {},
): UseFormPayload {
  const { customRequiredFields = [], validateOn } = options

  const serviceCategoryForm = useForm(
    defaultFormState,
    { entity: serviceCategory, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [serviceCategory.id, serviceCategory.cache_key],
  )

  return serviceCategoryForm
}

export function useRelations(serviceCategory: ServiceCategoryModel) {
  const { service_id } = serviceCategory || {}

  const services = useSelector(reduxState => reduxState.entities.services)

  const service: Partial<ServiceModel> = services[service_id] || {}

  return {
    service,
  }
}

function useServiceCategory(initEntity: Partial<ServiceCategoryModel> = {}) {
  // @ts-expect-error TS2322
  const { entity: serviceCategory }: { entity: ServiceCategoryModel} = useLatestEntity(initEntity, 'apiLogs')

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)
  const { showCreateOrEditServiceCategoryModal } = callbacks || {}

  const { creating, loading, updating } = useSelector(reduxState => reduxState.serviceCategories)

  const { service } = useRelations(serviceCategory)

  return {
    serviceCategory,
    callbacks: {
      createOrEditServiceCategory: () => createOrEditServiceCategory(
        { serviceCategory, showCreateOrEditServiceCategoryModal },
      ),
      createServiceCategory: (serviceCategoryParams: Partial<ServiceCategoryModel>) => (
        createServiceCategory({ serviceCategoryParams, dispatch })
      ),
      updateServiceCategory: (serviceCategoryParams: Partial<ServiceCategoryModel>, requestOptions: EntityOptions) => (
        updateServiceCategory({
          serviceCategory, serviceCategoryParams, dispatch, requestOptions,
        })
      ),
    },
    creating,
    loading,
    service,
    updating,
  }
}

export default useServiceCategory
